/**
 *  @class
 *  @function ImageTextOverlay
 */

if (!customElements.get('image-with-text-overlay')) {
  class ImageTextOverlay extends HTMLElement {
    constructor() {
      super();

      this.tl = false;
      this.splittext = false;
      this._ro = null;
      this._onWindowLoad = null;
      this._hasInitialized = false;
    }
    connectedCallback() {
      console.log('[ImageOverlay] connectedCallback fired');
      console.log('[ImageOverlay] animations-true:', document.body.classList.contains('animations-true'));
      
      if (document.body.classList.contains('animations-true')) {
        // Wait for GSAP and dependencies to load
        this.waitForDependencies();
      } else {
        console.log('[ImageOverlay] Animations disabled');
      }
    }
    
    waitForDependencies() {
      console.log('[ImageOverlay] Waiting for dependencies...');
      let attempts = 0;
      const maxAttempts = 50; // 50 attempts * 100ms = 5 seconds max wait
      
      const checkDependencies = () => {
        attempts++;
        console.log(`[ImageOverlay] Check attempt ${attempts} - gsap:`, typeof gsap !== 'undefined', 'ScrollTrigger:', typeof ScrollTrigger !== 'undefined', 'SplitText:', typeof SplitText !== 'undefined');
        
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined' && typeof SplitText !== 'undefined') {
          console.log('[ImageOverlay] All dependencies loaded! Starting animations...');
          this.prepareAnimationsWhenLaidOut();
        } else if (attempts >= maxAttempts) {
          console.warn('[ImageOverlay] Dependencies did not load after', maxAttempts, 'attempts. Showing content without animation.');
          this.showContentFallback();
        } else {
          // Check again in 100ms
          setTimeout(checkDependencies, 100);
        }
      };
      
      checkDependencies();
    }
    showContentFallback() {
      // Fallback to show content if animations fail to initialize
      console.log('[ImageOverlay] showContentFallback called - making all content visible');
      if (this.querySelector('h3')) {
        this.querySelector('h3').style.visibility = 'visible';
        console.log('[ImageOverlay] Made h3 visible');
      }
      this.querySelectorAll('.rte p').forEach((p) => {
        p.style.visibility = 'visible';
      });
      console.log('[ImageOverlay] Made', this.querySelectorAll('.rte p').length, 'paragraphs visible');
      this.querySelectorAll('.subheading, .button').forEach((el) => {
        el.style.opacity = '1';
      });
      console.log('[ImageOverlay] Made subheading and buttons visible');
    }
    disconnectedCallback() {
      console.log('[ImageOverlay] disconnectedCallback fired');
      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }
      if (this._onWindowLoad) {
        window.removeEventListener('load', this._onWindowLoad);
        this._onWindowLoad = null;
      }
      this._hasInitialized = false;
      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        if (this.tl) {
          this.tl.kill();
          console.log('[ImageOverlay] Timeline killed');
        }
        if (this.splittext) {
          this.splittext.revert();
          console.log('[ImageOverlay] SplitText reverted');
        }
      }
    }

    prepareAnimationsWhenLaidOut() {
      const section = this;

      const canLayout = () => {
        const r = section.getBoundingClientRect();
        return section.isConnected && r.width > 0 && r.height > 0 && section.offsetHeight > 0;
      };

      const tryStart = (reason) => {
        if (section._hasInitialized) {
          return;
        }
        const ok = canLayout();
        void reason;

        if (!ok) return;
        section._hasInitialized = true;
        if (section._ro) {
          section._ro.disconnect();
          section._ro = null;
        }
        if (section._onWindowLoad) {
          window.removeEventListener('load', section._onWindowLoad);
          section._onWindowLoad = null;
        }
        section.prepareAnimations();
      };

      // Try immediately and on the next paint. (Not a timer; aligns with layout.)
      tryStart('immediate');
      requestAnimationFrame(() => tryStart('raf'));

      // If we still have no box (common when hidden in a slider/tab), wait for real layout.
      if (!this._ro && typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(() => tryStart('resize'));
        this._ro.observe(this);
      }

      if (!this._onWindowLoad) {
        this._onWindowLoad = () => tryStart('window.load');
        window.addEventListener('load', this._onWindowLoad, { once: true });
      }
    }
    prepareAnimations() {
      console.log('[ImageOverlay] prepareAnimations started');
      let section = this,
        button_offset = 0,
        property = (gsap.getProperty("html", "--header-height") + gsap.getProperty("html", "--header-offset")) + 'px';

      // We'll check if element is in view AFTER layout settles
      let isAlreadyInView = false;

      section.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top bottom-=10%",
          // More aggressive start point to catch elements already visible
          once: true,
          onEnter: () => {
            console.log('[ImageOverlay] ScrollTrigger onEnter fired');
          }
        }
      });
      console.log('[ImageOverlay] Timeline created with ScrollTrigger');

      // Use Promise.resolve to ensure fonts are loaded, with longer fallback timeout
      // Also add a small delay to ensure everything is settled
      console.log('[ImageOverlay] Starting font load wait...');
      console.log('[ImageOverlay] document.fonts.status:', document.fonts.status);
      
      Promise.race([
        document.fonts.ready,
        new Promise(resolve => setTimeout(resolve, 1000))
      ]).then(function() {
        console.log('[ImageOverlay] Fonts ready (or timeout), waiting 50ms for stability');
        // Add a small delay to ensure layout is stable after font load
        return new Promise(resolve => setTimeout(resolve, 50));
      }).then(function() {
        console.log('[ImageOverlay] Starting SplitText');
        const elements = section.querySelectorAll('h3, p:not(.subheading)');
        console.log('[ImageOverlay] Elements to split:', elements.length);
        
        section.splittext = new SplitText(elements, {
          type: 'lines, words',
          linesClass: 'line-child'
        });
        console.log('[ImageOverlay] SplitText complete');

        if (section.querySelector('.subheading')) {
          console.log('[ImageOverlay] Adding subheading animation');
          section.tl
            .fromTo(section.querySelector('.subheading'), {
              opacity: 0
            }, {
              duration: 0.75,
              opacity: 0.6
            }, 0);

          button_offset += 0.5;
        }
        if (section.querySelector('h3')) {
          const h3Lines = section.querySelectorAll('h3 .line-child div');
          console.log('[ImageOverlay] Found', h3Lines.length, 'h3 line-child divs');
          let h3_duration = 0.8 + ((h3Lines.length - 1) * 0.08);
          console.log('[ImageOverlay] h3 animation duration:', h3_duration);
          section.tl
            .set(section.querySelector('h3'), {
              visibility: 'visible'
            }, 0)
            .from(h3Lines, {
              duration: h3_duration,
              yPercent: '100',
              stagger: 0.08
            }, 0);
          button_offset += h3_duration;
        }
        if (section.querySelector('.rte p')) {
          const pLines = section.querySelectorAll('.rte p .line-child div');
          console.log('[ImageOverlay] Found', pLines.length, 'paragraph line-child divs');
          let p_duration = 0.8 + ((pLines.length - 1) * 0.02);
          console.log('[ImageOverlay] p animation duration:', p_duration);
          section.tl
            .set(section.querySelectorAll('.rte p'), {
              visibility: 'visible'
            }, 0)
            .from(pLines, {
              duration: p_duration,
              yPercent: '100',
              stagger: 0.02
            }, 0);
          button_offset += p_duration;
        }
        if (section.querySelectorAll('.button').length > 0) {
          console.log('[ImageOverlay] Found', section.querySelectorAll('.button').length, 'buttons');
          let i = 1;
          section.querySelectorAll('.button').forEach((item) => {
            section.tl.fromTo(item, {
              autoAlpha: 0
            }, {
              duration: 0.5,
              autoAlpha: 1
            }, ((button_offset * 0.4) + (i - 1) * 0.1));
            i++;
          });
        }

        // Refresh ScrollTrigger to ensure proper positioning after font load and split
        console.log('[ImageOverlay] Refreshing ScrollTrigger');
        ScrollTrigger.refresh();
        console.log('[ImageOverlay] ScrollTrigger refreshed');
        
        // NOW check if element is in view (after layout has settled)
        const rect = section.getBoundingClientRect();
        isAlreadyInView = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
        
        console.log('[ImageOverlay] AFTER LAYOUT - Element position - top:', rect.top, 'bottom:', rect.bottom);
        console.log('[ImageOverlay] Window height:', window.innerHeight);
        console.log('[ImageOverlay] isAlreadyInView:', isAlreadyInView);
        
        // If element is already in view, check if ScrollTrigger will fire
        // If not, manually play it
        if (isAlreadyInView) {
          console.log('[ImageOverlay] Element already in view - checking ScrollTrigger state');
          
          // Use requestAnimationFrame to let ScrollTrigger refresh settle
          requestAnimationFrame(() => {
            // Check if the ScrollTrigger has already passed its start point
            const scrollTriggerInstance = section.tl.scrollTrigger;
            if (scrollTriggerInstance) {
              const progress = scrollTriggerInstance.progress;
              console.log('[ImageOverlay] ScrollTrigger progress:', progress);
              
              // If progress > 0, we're already in the trigger zone
              // ScrollTrigger won't fire onEnter if we're already inside
              if (progress > 0) {
                console.log('[ImageOverlay] Already inside trigger zone (progress > 0), manually playing timeline');
                section.tl.play(0);
              } else {
                console.log('[ImageOverlay] ScrollTrigger should fire automatically (progress is 0)');
              }
            }
          });
        } else {
          console.log('[ImageOverlay] Element not in view yet, waiting for scroll trigger');
        }
        
        console.log('[ImageOverlay] Animation setup complete!');

      }).catch(function(error) {
        // If animation fails, ensure content is still visible
        console.error('[ImageOverlay] Animation setup failed:', error);
        console.error('[ImageOverlay] Error stack:', error.stack);
        if (section.querySelector('h3')) {
          section.querySelector('h3').style.visibility = 'visible';
        }
        section.querySelectorAll('.rte p').forEach((p) => {
          p.style.visibility = 'visible';
        });
        section.querySelectorAll('.subheading, .button').forEach((el) => {
          el.style.opacity = '1';
        });
        console.log('[ImageOverlay] Fallback complete - content should be visible');
      });

      if (section.querySelector('.thb-parallax-image')) {
        console.log('[ImageOverlay] Setting up parallax animation');
        gsap.fromTo(section.querySelectorAll('.thb-parallax-image'), {
          y: '-8%'
        }, {
          y: '8%',
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            scrub: 1,
            start: () => `top bottom`,
            end: () => `bottom top+=${property}`,
            onUpdate: () => {
              property = (gsap.getProperty("html", "--header-height") + gsap.getProperty("html", "--header-offset")) + 'px';
            }
          }
        });
        console.log('[ImageOverlay] Parallax animation set up');
      }
    }
  }
  customElements.define('image-with-text-overlay', ImageTextOverlay);
}